// Schedule Service API Client

import { BaseApiClient } from '../../../apiClients/BaseApiClient';
import {
  AddParticipantRequest,
  AddTemplateParticipantRequest,
  AddTemplateParticipantResponse,
  CancelParticipationRequest,
  ConfirmParticipationRequest,
  CreateAdHocInstanceRequest,
  CreateScheduleRequest,
  CreateTemplateRequest,
  DeleteTemplateRequest,
  EventInstance,
  EventInstanceParticipant,
  EventSchedule,
  EventTemplate,
  GetEventInstanceResponse,
  GetEventInstancesParams,
  GetEventInstancesResponse,
  GetSchedulesParams,
  GetSchedulesResponse,
  GetTemplatesResponse,
  ReplaceParticipantRequest,
  ReplaceTemplateParticipantRequest,
  ReplaceTemplateParticipantResponse,
  UpdateEventInstanceRequest,
  UpdateEventInstanceResponse,
  UpdateParticipantRoleRequest,
  UpdateScheduleRequest,
  UpdateTemplateParticipantRoleRequest,
  UpdateTemplateParticipantRoleResponse,
  UpdateTemplateRequest,
} from './types';

class ScheduleApiClient extends BaseApiClient {
  constructor() {
    super({ prefix: 'schedule' });
  }

  // === EVENT SCHEDULES ===

  /**
   * Create a new event schedule
   */
  async createSchedule(request: CreateScheduleRequest): Promise<EventSchedule> {
    return this.makeRequest<EventSchedule>('/', {
      method: 'POST',
      data: request,
    });
  }

  /**
   * Get all schedules for the current workspace
   */
  async getSchedules(
    params?: GetSchedulesParams
  ): Promise<GetSchedulesResponse> {
    return this.makeRequest<GetSchedulesResponse>('/', {
      params,
    });
  }

  /**
   * Get a specific schedule by ID
   */
  async getSchedule(id: string): Promise<EventSchedule> {
    return this.makeRequest<EventSchedule>(`/${id}`);
  }

  /**
   * Update an existing schedule
   */
  async updateSchedule(
    id: string,
    request: UpdateScheduleRequest
  ): Promise<EventSchedule> {
    return this.makeRequest<EventSchedule>(`/${id}`, {
      method: 'PATCH',
      data: request,
    });
  }

  /**
   * Delete a schedule and all associated data
   */
  async deleteSchedule(id: string): Promise<void> {
    return this.makeRequest<void>(`/${id}`, {
      method: 'DELETE',
    });
  }

  // === EVENT TEMPLATES ===

  /**
   * Create a recurring event template
   */
  async createTemplate(request: CreateTemplateRequest): Promise<EventTemplate> {
    return this.makeRequest<EventTemplate>('/event-templates/', {
      method: 'POST',
      data: request,
    });
  }

  /**
   * Get all templates for a specific schedule
   */
  async getTemplatesBySchedule(
    scheduleIds?: string[]
  ): Promise<GetTemplatesResponse> {
    return this.makeRequest<GetTemplatesResponse>(`/event-templates/`, {
      params: {
        scheduleIds,
      },
    });
  }

  /**
   * Get a specific template by ID
   */
  async getTemplate(id: string): Promise<EventTemplate> {
    return this.makeRequest<EventTemplate>(`/event-templates/${id}`);
  }

  /**
   * Update an existing template
   */
  async updateTemplate(
    id: string,
    request: UpdateTemplateRequest
  ): Promise<EventTemplate> {
    return this.makeRequest<EventTemplate>(`/event-templates/${id}`, {
      method: 'PATCH',
      data: request,
    });
  }

  /**
   * Delete a template and handle future instances
   */
  async deleteTemplate(
    id: string,
    request: DeleteTemplateRequest
  ): Promise<void> {
    return this.makeRequest<void>(`/event-templates/${id}`, {
      method: 'DELETE',
      data: request,
    });
  }

  // === EVENT INSTANCES ===

  /**
   * Get event instances with optional filtering
   */
  async getEventInstances(
    params: GetEventInstancesParams
  ): Promise<GetEventInstancesResponse> {
    return this.makeRequest<GetEventInstancesResponse>('/event-instances/', {
      params: params,
    });
  }

  /**
   * Get a specific event instance by ID
   */
  async getEventInstance(id: string): Promise<GetEventInstanceResponse> {
    return this.makeRequest<GetEventInstanceResponse>(`/event-instances/${id}`);
  }

  /**
   * Create a one-time event instance
   */
  async createAdHocInstance(
    request: CreateAdHocInstanceRequest
  ): Promise<EventInstance> {
    return this.makeRequest<EventInstance>('/event-instances/ad-hoc', {
      method: 'POST',
      data: request,
    });
  }

  /**
   * Update an existing event instance
   */
  async updateEventInstance(
    id: string,
    request: UpdateEventInstanceRequest
  ): Promise<UpdateEventInstanceResponse> {
    return this.makeRequest<UpdateEventInstanceResponse>(
      `/event-instances/${id}`,
      {
        method: 'PATCH',
        data: request,
      }
    );
  }

  /**
   * Delete a specific event instance
   */
  async deleteEventInstance(id: string): Promise<void> {
    return this.makeRequest<void>(`/event-instances/${id}`, {
      method: 'DELETE',
    });
  }

  // === EVENT INSTANCE PARTICIPANTS ===

  /**
   * Get all participants for a specific event instance
   */
  async getEventInstanceParticipants(
    instanceId: string
  ): Promise<{ participants: EventInstanceParticipant[] }> {
    return this.makeRequest(`/event-instances/${instanceId}/participants`);
  }

  /**
   * Confirm user participation in an event instance
   */
  async confirmParticipation(
    instanceId: string,
    userId: string,
    request: ConfirmParticipationRequest
  ): Promise<EventInstanceParticipant> {
    return this.makeRequest<EventInstanceParticipant>(
      `/event-instances/${instanceId}/participants/${userId}/confirm`,
      {
        method: 'POST',
        data: request,
      }
    );
  }

  /**
   * Cancel user participation in an event instance
   */
  async cancelParticipation(
    instanceId: string,
    userId: string,
    request: CancelParticipationRequest
  ): Promise<EventInstanceParticipant> {
    return this.makeRequest<EventInstanceParticipant>(
      `/event-instances/${instanceId}/participants/${userId}/cancel`,
      {
        method: 'POST',
        data: request,
      }
    );
  }

  /**
   * Replace a participant with another user
   */
  async replaceParticipant(
    instanceId: string,
    userId: string,
    request: ReplaceParticipantRequest
  ): Promise<EventInstanceParticipant> {
    return this.makeRequest<EventInstanceParticipant>(
      `/event-instances/${instanceId}/participants/${userId}/replace`,
      {
        method: 'POST',
        data: request,
      }
    );
  }

  async addParticipant(
    instanceId: string,
    userId: string,
    request: AddParticipantRequest
  ): Promise<EventInstanceParticipant> {
    return this.makeRequest<EventInstanceParticipant>(
      `/event-instances/${instanceId}/participants/${userId}/add`,
      {
        method: 'POST',
        data: request,
      }
    );
  }

  /**
   * Permanently delete a participant from an event instance
   */
  async deleteParticipant(
    instanceId: string,
    userId: string
  ): Promise<EventInstanceParticipant> {
    return this.makeRequest<EventInstanceParticipant>(
      `/event-instances/${instanceId}/participants/${userId}/delete`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Update participant role (leader/participant)
   */
  async updateParticipantRole(
    instanceId: string,
    userId: string,
    request: UpdateParticipantRoleRequest
  ): Promise<EventInstanceParticipant> {
    return this.makeRequest<EventInstanceParticipant>(
      `/event-instances/${instanceId}/participants/${userId}/role`,
      {
        method: 'PATCH',
        data: request,
      }
    );
  }

  // === EVENT TEMPLATE PARTICIPANTS ===

  /**
   * Update participant role in a template (affects future instances)
   */
  async updateTemplateParticipantRole(
    templateId: string,
    participantId: string,
    request: UpdateTemplateParticipantRoleRequest
  ): Promise<UpdateTemplateParticipantRoleResponse> {
    return this.makeRequest<UpdateTemplateParticipantRoleResponse>(
      `/event-templates/${templateId}/participants/${participantId}/role`,
      {
        method: 'PATCH',
        data: request,
      }
    );
  }

  /**
   * Add participant to a template (affects future instances)
   */
  async addTemplateParticipant(
    templateId: string,
    request: AddTemplateParticipantRequest
  ): Promise<AddTemplateParticipantResponse> {
    return this.makeRequest<AddTemplateParticipantResponse>(
      `/event-templates/${templateId}/participants`,
      {
        method: 'POST',
        data: request,
      }
    );
  }

  /**
   * Delete participant from a template (affects future instances)
   */
  async deleteTemplateParticipant(
    templateId: string,
    participantId: string
  ): Promise<void> {
    return this.makeRequest<void>(
      `/event-templates/${templateId}/participants/${participantId}`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Replace participant in a template (affects future instances)
   */
  async replaceTemplateParticipant(
    templateId: string,
    participantId: string,
    request: ReplaceTemplateParticipantRequest
  ): Promise<ReplaceTemplateParticipantResponse> {
    return this.makeRequest<ReplaceTemplateParticipantResponse>(
      `/event-templates/${templateId}/participants/${participantId}/replace`,
      {
        method: 'POST',
        data: request,
      }
    );
  }
}

// Export singleton instance
export const scheduleApi = new ScheduleApiClient();
export default scheduleApi;
